import PropTypes from 'prop-types'
import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { t } from 'ttag'

import { Globals } from '@h2/globals'
import { Editor, tinymce } from '@h2/common/components/tinyMceBundle'

import { borderOutlineStyles } from '../styles'

const StyledEditorWrapper = styled.div`
  .mce-notification {
    display: none !important;
  }
  .tox-tinymce {
    ${borderOutlineStyles};
  }
`
const mediaPopupTitle = fileType => {
  switch (fileType) {
    case 'image':
      return t`Image Browser`
    case 'media':
      return t`Video Browser`
    default:
      return t`File Browser`
  }
}
/**
 *
 * The HtmlEditor component can be used to collect multiple lines of text and format it with basic styling.
 */
const HtmlEditor = ({
  value,
  onChange,
  isDisabled,
  onBlur,
  height = 300,
  noVideoField = false,
  disableSubscriberFeatures = false,
  showUserFiles = true
}) => {
  const { isSubscriber, user, defaultUrl } = useContext(Globals)
  const subscriberFeaturesEnabled = useMemo(
    () => !disableSubscriberFeatures && (isSubscriber || user.isRoot),
    [disableSubscriberFeatures, isSubscriber, user.isRoot]
  )
  const toolbarsUsed = `bold italic | alignleft aligncenter alignright alignjustify | bullist numlist 
  ${
    subscriberFeaturesEnabled
      ? noVideoField
        ? '| link image '
        : '| link image media '
      : ''
  }| code`
  const getOptions = useCallback(
    isPrivate => {
      const stripStylesOnPaste = input => {
        const stringStripper = /(style|dir|id|role|aria-level)=".*?"/g

        return input.replace(stringStripper, ' ')
      }
      const options = {
        resize: 'both',
        width: '100%',
        menubar: false, //"edit insert view format table",
        plugins: 'lists media link image code',
        paste_remove_styles_if_webkit: true,
        keep_styles: false,
        paste_postprocess: (plugin, args) => {
          args.node.innerHTML = stripStylesOnPaste(args.node.innerHTML)
        },
        setup(editor) {
          editor.on('change', function () {
            editor.save()
          })
        },
        extended_valid_elements: 'form [onsubmit|id|action|class|method]',
        file_picker_callback: (callback, initValue, meta) => {
          //(field_name, url, type, win) {
          const cmsURL = isPrivate
            ? `/cms/user/files?type=${meta.filetype}`
            : `/cms/tenant/files?type=${meta.filetype}` // script URL - use an absolute path!
          const title = mediaPopupTitle(meta.fileType)

          tinymce.activeEditor.windowManager.openUrl({
            title,
            url: cmsURL,
            onMessage(dialogApi, details) {
              if (details.mceAction === 'setFileUrl') {
                callback(details.content)
                tinymce.activeEditor.windowManager.close()
              }
            }
          })

          return false
        },
        branding: false,
        statusbar: false,
        remove_script_host: true,
        relative_urls: false,
        allow_script_urls: true,
        document_base_url: `${defaultUrl}`,
        content_css: [
          'https://use.typekit.net/rda0bts.css',
          `${defaultUrl}/css/tenant/tiny_mce_headed2render.css`
        ],
        toolbar: toolbarsUsed
      }

      return options
    },
    [toolbarsUsed, defaultUrl]
  )
  /**
   * this onChange event doesn't receive a javascript event, but the form handler looks for one
   * so we return it in the same format.
   */
  const doOnChange = useCallback(
    textValue => {
      onChange({
        target: {
          value: textValue
        }
      })
    },
    [onChange]
  )

  return (
    <StyledEditorWrapper>
      <Editor
        init={getOptions(showUserFiles)}
        height={height}
        disabled={isDisabled}
        value={value}
        onEditorChange={doOnChange}
        onBlur={onBlur}
      />
    </StyledEditorWrapper>
  )
}

HtmlEditor.propTypes = {
  /** The value of the textarea when controlled */
  value: PropTypes.string,
  /** The callback when the user types in the textarea */
  onChange: PropTypes.func,
  /** The callback when the textarea loses focus */
  onBlur: PropTypes.func,
  /** The number of rows for the textarea */
  height: PropTypes.number,
  /** Should files be listed from the user or for the tenant */
  isPrivate: PropTypes.string,
  /** Whether the text input is disbaled or not */
  isDisabled: PropTypes.bool,
  /** Whether the video input is removed or not */
  noVideoField: PropTypes.bool,
  /** Certain menus are enabled for subscribers, but there may be occasions when that is not desired */
  disableSubscriberFeatures: PropTypes.bool,
  /** When listing files, should this show the user's files or the tenant's global files -- defaults to true*/
  showUserFiles: PropTypes.bool
}

export { HtmlEditor, mediaPopupTitle }
